import React, { useState } from 'react';
import axios from 'axios';

import CardDialog from '~/components/CardDialog';
import { useClaim } from '~/components/ClaimContainer';
import WithConfirm from '~/components/ConfirmModal';
import { PERMISSION_ACTIONS, PERMISSION_VERBS, RestrictedPermissions } from '~/components/core';
import Chip from '~/components/core/Atomic/Chip/Chip';
import { PencilIcon, TrashIcon } from '~/components/icons';
import InlineIconButton from '~/components/InlineIconButton';
import type { CoveredObjectsResponseCoveredObjectsItem as CoveredObjectRow } from '~/server_shared/openapi/data-contracts';
import { isClaimPolicyManuallyFilled, reportAxiosError } from '~/Utils';

import CoveredEntityCard from '../CoveredEntity/CoveredEntityCard';
import EditCoveredEntityDialog from '../CoveredEntity/EditCoveredEntityDialog/EditCoveredEntityDialog';

type CoveredEntity = Record<string, unknown> & {
  display_name?: string;
  vehicle?: { display_name?: string };
};

// Pick the most-specific display name available: the freshly-loaded
// coveredEntity wins after an edit, then the original row (covering both v1
// non-auto rows where display_name is on the root and auto rows where the
// label lives under .vehicle).
const pickDisplayName = (entity: CoveredEntity | null, row: CoveredObjectRow): string | undefined =>
  entity?.display_name || entity?.vehicle?.display_name || row?.display_name || row?.vehicle?.display_name;

type FieldConfigItem = { id: string; desc?: string; type?: string; value?: unknown; [key: string]: unknown };

interface CoveredObjectViewDialogProps {
  row: CoveredObjectRow | null;
  policyId?: number;
  icon?: React.ReactNode;
  coveredObjectTypeLabel: string;
  defaultFields: FieldConfigItem[];
  configuredFields: FieldConfigItem[];
  onEdited?: (updated: CoveredObjectRow) => void;
  onMutated?: () => void;
  onClose: () => void;
}

// View dialog for a covered object. The outer modal owns the title bar
// (icon + name + First Party chip + edit + trash + X) so the close button
// lines up with the entity title instead of floating in its own row above.
// The body is the original CoveredEntityCard with hideHeader=true so it
// renders just the field grid + Coverages without its own title row.
//
// Editing and deleting are reimplemented here rather than reusing the
// internal pencil on CoveredEntityCard — moving those actions out of the
// inner card is the whole point. After a successful edit we bump
// `refreshKey` so the inner card re-fetches per-entity coverages without
// remounting; `onEdited` updates the host's view of the row (so the field
// grid renders the post-edit values immediately); and `onMutated` signals
// the per-type list to re-fetch (so the row's listing reflects the change
// — same hook on the delete path, which is the one that was breaking
// before).
const CoveredObjectViewDialog: React.FC<CoveredObjectViewDialogProps> = ({
  row,
  policyId,
  icon,
  coveredObjectTypeLabel,
  defaultFields,
  configuredFields,
  onEdited,
  onMutated,
  onClose,
}) => {
  const { claim, onAsyncClaimUpdate } = useClaim({ claimRequired: true });
  const isManualPolicy = isClaimPolicyManuallyFilled(claim);
  const policyPrefix = isManualPolicy ? '' : 'api_';
  // Prefer the row's own policy_id (the paginated response carries it per
  // row) and only fall back to the prop / claim — keeps the entity fetch
  // consistent with the inline-card path that reads row.policy_id.
  const effectivePolicyId = row?.policy_id ?? policyId ?? claim?.policy?.id;

  const [showEditDialog, setShowEditDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [coveredEntity, setCoveredEntity] = useState<CoveredEntity | null>(null);

  if (!row || effectivePolicyId === null || effectivePolicyId === undefined) {
    // Without a policyId we'd issue `/policies/undefined/covered_entities/…`.
    // Claims with no policy don't have a covered-object table view to begin
    // with; bail out before mounting the modal rather than firing a bad fetch.
    return null;
  }

  const cardHeaderTitle = pickDisplayName(coveredEntity, row) || coveredObjectTypeLabel;

  const title = (
    <span className="flex items-center">
      {icon}
      <span className="ml-16">{cardHeaderTitle}</span>
      {row.is_first_party && (
        <span className="ml-12">
          <Chip size="small" label="First Party Involved" className="m-4 font-normal" />
        </span>
      )}
    </span>
  );

  const handleEntityUpdate = async (values: Record<string, unknown>) => {
    setIsSaving(true);
    try {
      await axios.patch(`/api/v1/claims/${claim.id}/${policyPrefix}policy/covered_objects/${row.id}`, values);
      await onAsyncClaimUpdate();
      setRefreshKey((value) => value + 1);
      setShowEditDialog(false);
      // Hand the host the updated values so it can replace the list row
      // backing this dialog — without that, the field grid + header keep
      // rendering the pre-edit values until the modal is closed.
      onEdited?.({ ...row, ...values });
      onMutated?.();
    } catch (error) {
      reportAxiosError(error);
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemove = async () => {
    setIsSaving(true);
    const url = `/api/v1/claims/${claim.id}/${policyPrefix}policy/covered_objects/${row.id}`;
    try {
      // A claim-owned object goes back to unlisted rather than being deleted, so its
      // coverages and exposures survive.
      await (row.is_claim_owned ? axios.patch(`${url}/set_unlisted`) : axios.delete(url));
      await onAsyncClaimUpdate();
      onMutated?.();
      onClose();
    } catch (error) {
      reportAxiosError(error);
    } finally {
      setIsSaving(false);
    }
  };

  // `useIconButton` wraps the icon in MUI's IconButton so the edit/trash
  // buttons match the size + click area of the close X (which CardDialog
  // renders as an FsIconButton/IconButton). Without it, the raw 16x16
  // icons sit on a different baseline than the close button.
  const action = (
    <RestrictedPermissions
      action={isManualPolicy ? PERMISSION_ACTIONS.MANUAL_POLICY : PERMISSION_ACTIONS.API_POLICY}
      verb={PERMISSION_VERBS.WRITE}
    >
      <InlineIconButton
        icon={PencilIcon}
        useIconButton
        onClick={() => setShowEditDialog(true)}
        tooltipTitle={coveredEntity ? `Edit ${coveredObjectTypeLabel}` : `Loading ${coveredObjectTypeLabel}...`}
        disabled={!coveredEntity || isSaving}
      />
      <WithConfirm
        title={`Remove this ${coveredObjectTypeLabel}?`}
        contentText={`Are you sure you want to remove this ${coveredObjectTypeLabel} from the policy?`}
        primaryButtonName="Yes, Remove"
        shouldCloseOnPrimary
      >
        <InlineIconButton
          icon={TrashIcon}
          useIconButton
          onClick={handleRemove}
          tooltipTitle={`Remove ${coveredObjectTypeLabel} from policy`}
          disabled={isSaving}
        />
      </WithConfirm>
    </RestrictedPermissions>
  );

  // Once the per-entity fetch returns, fold its data over the original row
  // so the card renders the post-edit values; before that, fall back to the
  // row payload from the listing.
  const coveredObject = {
    ...row,
    ...(coveredEntity ?? {}),
    id: row.id,
    policy_id: effectivePolicyId,
    covered_entity_id: row.covered_entity_id,
  };

  return (
    <>
      <CardDialog
        isDialog
        open
        onClose={onClose}
        maxWidth="xl"
        fullWidth
        title={title}
        action={action}
        // CardDialog hard-codes top:5px on the action area so the close X
        // sits 5px lower than the title's vertical center. Nudge the title
        // box down by the same amount to bring them onto the same baseline.
        headerStyle={{ paddingTop: 29, paddingBottom: 12 }}
      >
        <CoveredEntityCard
          icon={icon}
          coveredObjectTypeLabel={coveredObjectTypeLabel}
          defaultFields={defaultFields}
          configuredFields={configuredFields}
          coveredObjectIdForPatchEndpoints={row.id}
          coveredObject={coveredObject}
          isFirstParty={!!row.is_first_party}
          fetchCoveredEntityEndpoint={`/api/v1/policies/${effectivePolicyId}/covered_entities/${row.covered_entity_id}`}
          hideHeader
          // Full-screen split: details on the left (all fields, no Show
          // More/Less), Coverages on the right and open by default.
          splitLayout
          showAllFields
          coveragesDefaultOpen
          onCoveredEntityLoaded={setCoveredEntity}
          refreshKey={refreshKey}
        />
      </CardDialog>
      {showEditDialog && coveredEntity && (
        <EditCoveredEntityDialog
          icon={icon}
          coveredObjectTypeLabel={coveredObjectTypeLabel}
          defaultFields={defaultFields}
          configuredFields={configuredFields}
          coveredEntity={coveredEntity}
          onSaveClick={handleEntityUpdate}
          onClose={() => !isSaving && setShowEditDialog(false)}
          isSaving={isSaving}
          isUpdate
        />
      )}
    </>
  );
};

export default CoveredObjectViewDialog;
